import {getSvgIconTemplate} from '../modules/svgIconBuilder.js';

/**
 * Input Search Group Component
 * Handles dynamic search with JSON responses and flexible result rendering
 */

class InputSearchGroup {
    /**
     * The main component wrapper element.
     * @type {HTMLElement}
     */
    element;

    /**
     * The input element ID.
     * @type {string}
     */
    inputId;

    /**
     * The search input element.
     * @type {HTMLInputElement|null}
     */
    input = null;

    /**
     * The results container element.
     * @type {HTMLElement|null}
     */
    resultsContainer = null;

    /**
     * Overlay element that renders the focus ring across input and results panel.
     * @type {HTMLElement|null}
     */
    ringOverlay = null;

    /**
     * The container wrapping the input and dropdown.
     * @type {HTMLElement|null}
     */
    dropdownContainer = null;

    /**
     * Indicates if the dropdown is currently open.
     * @type {boolean}
     */
    isOpen = false;

    /**
     * @param {HTMLElement} element The component's root element.
     */
    constructor(element) {
        this.element = element;
        this.inputId = element.dataset.inputId;
        this.searchRoute = element.dataset.searchRoute;
        this.minSearchLength = parseInt(element.dataset.minSearchLength) || 2;
        this.searchDelay = parseInt(element.dataset.searchDelay) || 300;
        this.noResultsText = element.dataset.noResultsText;
        this.noResultsHintText = element.dataset.noResultsHintText;
        this.loadingText = element.dataset.loadingText;
        this.errorText = element.dataset.errorText;
        this.untitledText = element.dataset.untitledText;

        this.input = document.getElementById(this.inputId);
        this.resultsContainer = document.getElementById(`search-results-${this.inputId}`);
        this.loadingIndicator = document.getElementById(`search-loading-${this.inputId}`);
        this.searchIcon = document.getElementById(`search-icon-${this.inputId}`);
        this.ringOverlay = element.querySelector('[data-ring-overlay]');
        this.dropdownContainer = element.querySelector('[data-dropdown-container]');

        this.searchTimeout = null;
        this.currentRequest = null;
        this.isOpen = false;

        this.init();
    }
    
    init() {
        if (!this.input || !this.resultsContainer || !this.searchRoute) {
            return;
        }
        
        // Position dropdown correctly relative to input field
        this.positionDropdown();
        
        // Bind events
        this.input.addEventListener('input', this.handleInput.bind(this));
        this.input.addEventListener('focus', this.handleFocus.bind(this));
        this.input.addEventListener('keydown', this.handleKeydown.bind(this));
        
        // Handle clear button
        const clearButton = this.element.querySelector('[data-clear-button]');
        if (clearButton) {
            clearButton.addEventListener('click', this.handleClear.bind(this));
        }
        
        // Click outside to close
        document.addEventListener('click', this.handleClickOutside.bind(this));
        
        // Handle result clicks
        this.resultsContainer.addEventListener('click', this.handleResultClick.bind(this));
        
        // Reposition on window resize
        window.addEventListener('resize', this.positionDropdown.bind(this));
    }
    
    positionDropdown() {
        // Find the input field container using the data attribute
        const inputContainer = this.element.querySelector('[data-input-container]');
        if (inputContainer) {
            this.resultsContainer.style.left = '0';
            this.resultsContainer.style.right = '0';
            this.resultsContainer.style.width = '100%';
        }
    }
    
    handleInput(event) {
        const query = event.target.value.trim();
        
        // Clear existing timeout
        if (this.searchTimeout) {
            clearTimeout(this.searchTimeout);
        }
        
        // Cancel existing request
        if (this.currentRequest) {
            this.currentRequest.abort();
            this.currentRequest = null;
        }
        
        // Hide results if query is too short
        if (query.length < this.minSearchLength) {
            this.hideResults();
            return;
        }
        
        // Debounce search
        this.searchTimeout = setTimeout(() => {
            this.performSearch(query);
        }, this.searchDelay);
    }
    
    handleFocus(event) {
        const query = event.target.value.trim();
        if (query.length >= this.minSearchLength && this.resultsContainer.children.length > 0) {
            this.showResults();
        }
    }
    
    handleKeydown(event) {
        if (!this.isOpen) return;
        
        const items = this.resultsContainer.querySelectorAll('.js-search-result-item');
        const currentFocus = this.resultsContainer.querySelector('.js-search-result-item.focused');
        let currentIndex = currentFocus ? [...items].indexOf(currentFocus) : -1;
        
        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                currentIndex = Math.min(currentIndex + 1, items.length - 1);
                this.focusItem(items[currentIndex]);
                break;
                
            case 'ArrowUp':
                event.preventDefault();
                currentIndex = Math.max(currentIndex - 1, 0);
                this.focusItem(items[currentIndex]);
                break;
                
            case 'Enter':
                event.preventDefault();
                if (currentFocus) {
                    const link = currentFocus.querySelector('a');
                    if (link && link.href) {
                        window.location.href = link.href;
                    }
                }
                break;
                
            case 'Escape':
                event.preventDefault();
                this.hideResults();
                this.input.blur();
                break;
        }
    }
    
    handleClickOutside(event) {
        if (!this.element.contains(event.target)) {
            this.hideResults();
        }
    }
    
    handleResultClick() {
        // Let default link behavior handle navigation
        this.hideResults();
    }
    
    handleClear() {
        // Clear the input value
        this.input.value = '';
        
        // Hide results if they're open
        if (this.isOpen) {
            this.hideResults();
        }
        
        // Cancel any pending search
        if (this.searchTimeout) {
            clearTimeout(this.searchTimeout);
            this.searchTimeout = null;
        }
        
        // Cancel existing request
        if (this.currentRequest) {
            this.currentRequest.abort();
            this.currentRequest = null;
        }
        
        // Focus back on the input
        this.input.focus();
        
        // Trigger input event to update any listeners
        this.input.dispatchEvent(new Event('input', {bubbles: true}));
    }
    
    focusItem(item) {
        // Remove previous focus
        this.resultsContainer.querySelectorAll('.js-search-result-item.focused').forEach(element => {
            element.classList.remove('focused', 'bg-blue-50');
        });
        
        // Add focus to current item
        if (item) {
            item.classList.add('focused', 'bg-blue-50');
            item.scrollIntoView({block: 'nearest'});
        }
    }
    
    async performSearch(query) {
        this.showLoading();
        
        try {
            const url = new URL(this.searchRoute, window.location.origin);
            url.searchParams.set('search', query);
            
            this.currentRequest = new AbortController();
            
            const response = await fetch(url.toString(), {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                },
                signal: this.currentRequest.signal,
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const data = await response.json();
            this.renderResults(data, query);
            
        } catch (error) {
            if (error.name !== 'AbortError') {
                this.renderError();
            }
        } finally {
            this.hideLoading();
            this.currentRequest = null;
        }
    }
    
    renderResults(data, query) {
        const hasResults = this.hasResults(data);
        
        if (hasResults) {
            this.resultsContainer.innerHTML = this.buildResultsHTML(data);
            this.showResults();
        } else {
            this.resultsContainer.innerHTML = this.buildNoResultsHTML(query);
            this.showResults();
        }
    }
    
    renderError() {
        const textClass = 'text-(--control-text-subtle)';

        this.resultsContainer.innerHTML = `
            <div class="py-32 text-center">
                <div class="flex flex-col items-center gap-8">
                    <svg class="w-48 h-48 fg-graphic-status-danger-default" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd"></path>
                    </svg>
                    <p class="fg-body-compact-md ${textClass}">${this.escapeHtml(this.errorText)}</p>
                </div>
            </div>
        `;
        this.showResults();
    }
    
    hasResults(data) {
        if (data.sections && Array.isArray(data.sections)) {
            return data.sections.some(section => section.items && section.items.length > 0);
        }
        if (data.items && Array.isArray(data.items)) {
            return data.items.length > 0;
        }
        return false;
    }
    
    buildResultsHTML(data) {
        let html = '<div class="flex flex-col gap-10">';
        
        // Handle sections format
        if (data.sections && Array.isArray(data.sections)) {
            data.sections.forEach(section => {
                if (section.items && section.items.length > 0) {
                    html += this.buildSectionHTML(section);
                }
            });
        }
        // Handle flat items format
        else if (data.items && Array.isArray(data.items)) {
            html += this.buildSectionHTML({items: data.items});
        }
        
        html += '</div>';
        return html;
    }
    
    buildSectionHTML(section) {
        const borderClass = 'border-(--control-border-subtle)';
        const titleClass = 'text-gray-400';

        let html = '<div>';

        // Section title
        if (section.title) {
            html += `
                <div class="px-10 py-8 border-b ${borderClass}">
                    <h3 class="${titleClass} fg-body-compact-md">${this.escapeHtml(section.title)}</h3>
                </div>
            `;
        }

        // Section items
        html += '<div class="py-4">';
        section.items.forEach(item => {
            html += this.buildItemHTML(item);
        });
        html += '</div>';

        html += '</div>';

        return html;
    }
    
    buildItemHTML(item) {
        const hasUrl = item.url && item.url.trim();
        const hasIcon = item.icon && item.icon.trim();
        const hasAvatar = item.avatar && item.avatar.trim();
        const hasSubtitle = item.subtitle && item.subtitle.trim();
        const hasBadge = item.badge && item.badge.trim();
        const hasDescription = item.description && item.description.trim();

        const title = item.title || this.untitledText;
        const itemId = item.id || '';

        const groupHoverClass = 'hover:bg-(--control-bg-hover)';
        const itemHoverClass = 'hover:bg-(--control-bg-hover)';
        const titleClass = 'text-(--control-text)';
        const subtitleClass = 'text-(--control-text-muted)';
        const descriptionClass = 'text-(--control-text-muted)';
        const badgeBgClass = 'bg-(--control-bg-muted)';
        const badgeTextClass = 'text-(--control-text-subtle)';

        let html = `<div class="group ${groupHoverClass}">`;

        // Wrapper (link or div)
        if (hasUrl) {
            html += `<a href="${this.escapeHtml(item.url)}" class="flex items-center gap-8 px-10 py-8 ${itemHoverClass} transition-colors duration-150 js-search-result-item" data-search-item-id="${this.escapeHtml(itemId)}">`;
        } else {
            html += `<div class="flex items-center gap-8 px-10 py-8 js-search-result-item" data-search-item-id="${this.escapeHtml(itemId)}">`;
        }

        // Icon or Avatar
        html += '<div class="flex-shrink-0">';
        if (hasAvatar) {
            html += `<img src="${this.escapeHtml(item.avatar)}" alt="${this.escapeHtml(title)}" class="w-32 h-32 fg-rounded-full object-cover">`;
        } else if (hasIcon) {
            const icon = this.escapeHtml(item.icon);
            html += `<div class="w-32 h-32 flex items-center justify-center">${this.mutedIconBox(getSvgIconTemplate(icon, {size: 'full'}))}</div>`;
        } else {
            html += `<div class="w-32 h-32 flex items-center justify-center">${this.mutedIconBox(getSvgIconTemplate('file-text', {size: 'full'}))}</div>`;
        }
        html += '</div>';

        // Content
        html += '<div class="flex-1 min-w-0">';
        html += '<div class="flex items-center gap-8">';
        html += `<h4 class="fg-body-compact-md ${titleClass} truncate cursor-pointer transition-colors">${this.escapeHtml(title)}</h4>`;

        if (hasBadge) {
            html += `<span class="inline-flex items-center px-8 py-2 fg-rounded-full fg-body-xs ${badgeBgClass} ${badgeTextClass}">${this.escapeHtml(item.badge)}</span>`;
        }

        html += '</div>';

        if (hasSubtitle) {
            html += `<p class="fg-body-sm ${subtitleClass} truncate mt-2">${this.escapeHtml(item.subtitle)}</p>`;
        }

        if (hasDescription) {
            html += `<p class="fg-body-sm ${descriptionClass} line-clamp-2 mt-4">${this.escapeHtml(item.description)}</p>`;
        }

        html += '</div>';

        // Arrow indicator for links
        if (hasUrl) {
            html += `<div class="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">${this.mutedIconBox(getSvgIconTemplate('chevron-right', {size: 'full'}))}</div>`;
        }

        // Close wrapper
        if (hasUrl) {
            html += '</a>';
        } else {
            html += '</div>';
        }

        html += '</div>';
        return html;
    }
    
    buildNoResultsHTML(query) {
        const textClass = 'text-(--control-text-subtle)';
        const subtextClass = 'text-(--control-text-muted)';

        return `
            <div class="py-32 text-center">
                <div class="flex flex-col items-center gap-8">
                    <span class="flex size-48 text-gray-300">${getSvgIconTemplate('search', {size: 'full'})}</span>
                    <div>
                        <p class="fg-body-compact-md ${textClass}">${this.escapeHtml(this.noResultsText)}</p>
                        ${query ? `<p class="fg-body-sm ${subtextClass} mt-4">${this.escapeHtml(this.noResultsHintText)}</p>` : ''}
                    </div>
                </div>
            </div>
        `;
    }
    
    /**
     * Sizes a full-size icon to 20 px, in the muted control color.
     * @param {string} icon The markup of a full-size icon
     * @returns {string}
     */
    mutedIconBox(icon) {
        return `<span class="flex size-20 text-(--control-text-muted)">${icon}</span>`;
    }
    
    /**
     * Escapes text for both element content and double-quoted attribute values in the markup templates above.
     * @param {string} text
     * @returns {string}
     */
    escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML.replaceAll('"', '&quot;');
    }
    
    showLoading() {
        if (this.loadingIndicator && this.searchIcon) {
            this.loadingIndicator.classList.remove('hidden');
            this.searchIcon.classList.add('hidden');
        }
    }
    
    hideLoading() {
        if (this.loadingIndicator && this.searchIcon) {
            this.loadingIndicator.classList.add('hidden');
            this.searchIcon.classList.remove('hidden');
        }
    }
    
    showResults() {
        this.element.setAttribute('data-state', 'open');
        this.isOpen = true;

        requestAnimationFrame(() => {
            this.updateRingOverlay();
        });
    }

    hideResults() {
        this.element.removeAttribute('data-state');
        this.isOpen = false;

        if (this.ringOverlay) {
            this.ringOverlay.style.height = '';
        }
    }

    /**
     * Expands the ring overlay so it wraps the input and the open results panel.
     */
    updateRingOverlay() {
        if (!this.ringOverlay || !this.resultsContainer || !this.input) {
            return;
        }

        const inputRect = this.input.getBoundingClientRect();
        const resultsRect = this.resultsContainer.getBoundingClientRect();
        const totalHeight = resultsRect.bottom - inputRect.top;

        if (totalHeight > 0) {
            this.ringOverlay.style.height = `${totalHeight}px`;
        }
    }
}

// Initialize all search input groups
document.addEventListener('DOMContentLoaded', function() {
    const searchGroups = document.querySelectorAll('[data-search-input-group]');
    searchGroups.forEach(element => {
        new InputSearchGroup(element);
    });
});
